<template>
    <div class="min-h-screen bg-gray-50">
        <div class="container mx-auto p-2 sm:p-6">
            <!-- 공지사항 -->
            <div class="bg-white rounded-xl shadow-sm overflow-hidden">
                <div class="flex justify-between items-center p-3 border-b">
                    <h2 class="text-[13px] sm:text-[15px] font-semibold">공지사항</h2>
                </div>
                <div class="divide-y divide-gray-100">
                    <!-- 로딩 상태 -->
                    <div v-if="isLoading" class="p-6 flex justify-center items-center">
                        <v-progress-circular indeterminate color="primary"></v-progress-circular>
                    </div>
                    <!-- 공지사항 목록 -->
                    <template v-else>
                        <div v-for="notice in paginatedNotices" :key="notice.id" @click="openDetail(notice)"
                            class="group hover:bg-blue-50 transition-all duration-200 cursor-pointer">
                            <div class="p-3">
                                <div class="flex items-center justify-between">
                                    <div class="flex items-center min-w-0 space-x-1">
                                        <div v-if="isNew(notice.date)"
                                            class="flex-shrink-0 bg-red-500 text-white text-[10px] font-semibold px-1 py-0.5 rounded-full leading-none">
                                            NEW
                                        </div>
                                        <h3
                                            class="truncate font-medium text-gray-900 group-hover:text-blue-600 text-[13px] sm:text-[15px]">
                                            {{ notice.title }}
                                        </h3>
                                        <div v-if="notice.has_attachment" class="text-gray-400 flex-shrink-0"
                                            @click.stop>
                                            <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none"
                                                viewBox="0 0 24 24" stroke="currentColor">
                                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                                    d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                                            </svg>
                                        </div>
                                    </div>
                                    <span :class="notice.is_read ? 'text-gray-500' : 'text-blue-500'"
                                        class="text-[10px] sm:text-xs font-medium whitespace-nowrap ml-2">
                                        {{ notice.is_read ? '읽음' : '안읽음' }}
                                    </span>
                                </div>
                                <div class="mt-1 flex items-center space-x-3 text-[11px] sm:text-xs text-gray-500">
                                    <span class="flex items-center">
                                        <svg xmlns="http://www.w3.org/2000/svg" class="h-3 w-3 mr-1" fill="none"
                                            viewBox="0 0 24 24" stroke="currentColor">
                                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                                d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                        </svg>
                                        {{ notice.admin_name }}
                                    </span>
                                    <span class="flex items-center">
                                        <svg xmlns="http://www.w3.org/2000/svg" class="h-3 w-3 mr-1" fill="none"
                                            viewBox="0 0 24 24" stroke="currentColor">
                                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                                d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                        </svg>
                                        {{ formatDate(notice.date) }}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </template>
                </div>
                <div class="flex justify-center items-center p-3 border-t">
                    <v-pagination v-model="currentNoticePage" :length="totalNoticePages"
                        :total-visible="getTotalVisible" density="compact" color="grey-darken-1" class="pagination-text"
                        size="small" @update:model-value="handlePageChange"></v-pagination>
                </div>
            </div>
        </div>

        <!-- 상세 보기 사이드 패널 -->
        <transition name="fade">
            <div v-if="selectedNotice" class="fixed inset-0 z-40" @click="closeDetail"></div>
        </transition>
        <transition name="slide">
            <div v-if="selectedNotice"
                class="fixed inset-y-0 right-0 w-full sm:w-[80%] md:w-[70%] lg:w-[60%] xl:w-[50%] 2xl:w-[45%] bg-white shadow-2xl overflow-y-auto z-50">
                <div class="p-3 sm:p-6">
                    <div class="flex justify-between items-center mb-2 sm:mb-4">
                        <h2 class="text-sm sm:text-base md:text-lg font-bold text-gray-800">{{ selectedNotice.title }}
                        </h2>
                        <button @click="closeDetail" class="text-gray-500 hover:text-gray-700">
                            <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 sm:h-4 sm:w-4 md:h-5 md:w-5"
                                fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                    d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                    </div>

                    <div class="flex items-center space-x-3 sm:space-x-4 mb-3 sm:mb-4">
                        <span class="flex items-center text-[11px] sm:text-xs md:text-sm text-gray-500">
                            <svg xmlns="http://www.w3.org/2000/svg" class="h-3 w-3 sm:h-3.5 sm:w-3.5 md:h-4 md:w-4 mr-1"
                                fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                    d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                            </svg>
                            {{ formatDate(selectedNotice.date) }}
                        </span>
                        <span v-if="selectedNotice.admin_name"
                            class="flex items-center text-[11px] sm:text-xs md:text-sm text-gray-500">
                            <svg xmlns="http://www.w3.org/2000/svg" class="h-3 w-3 sm:h-3.5 sm:w-3.5 md:h-4 md:w-4 mr-1"
                                fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                    d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                            </svg>
                            {{ selectedNotice.admin_name }}
                        </span>
                    </div>

                    <!-- 첨부파일 목록 -->
                    <div v-if="selectedNotice.attachments && selectedNotice.attachments.length > 0"
                        class="mb-4 sm:mb-6">
                        <h3 class="text-xs sm:text-sm md:text-base font-semibold mb-2 sm:mb-3">첨부파일</h3>
                        <div class="space-y-1.5 sm:space-y-2">
                            <div v-for="attachment in selectedNotice.attachments" :key="attachment.id"
                                @click="downloadAttachment(attachment)"
                                class="flex items-center justify-between p-2 sm:p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors duration-200 cursor-pointer">
                                <div class="flex items-center space-x-2">
                                    <svg xmlns="http://www.w3.org/2000/svg"
                                        class="h-3.5 w-3.5 sm:h-4 sm:w-4 md:h-5 md:w-5 text-gray-400" fill="none"
                                        viewBox="0 0 24 24" stroke="currentColor">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                            d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                                    </svg>
                                    <span class="text-[11px] sm:text-xs md:text-sm text-gray-700">{{
                                        attachment.original_name
                                    }}</span>
                                </div>
                                <div
                                    class="inline-flex items-center px-2 py-1 sm:px-2.5 sm:py-1 md:px-3 md:py-1.5 text-[10px] sm:text-xs font-medium text-blue-600 hover:text-blue-700">
                                    <svg xmlns="http://www.w3.org/2000/svg"
                                        class="h-3 w-3 sm:h-3.5 sm:w-3.5 md:h-4 md:w-4 mr-1" fill="none"
                                        viewBox="0 0 24 24" stroke="currentColor">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                            d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                                    </svg>
                                    다운로드
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="prose max-w-none text-[12px] sm:text-xs md:text-sm lg:text-base leading-relaxed sm:leading-relaxed"
                        v-html="selectedNotice.content"></div>

                    <!-- 하단 닫기 버튼 -->
                    <div class="sticky bottom-0 left-0 right-0 bg-white border-t p-2.5 sm:p-4 mt-4 sm:mt-6">
                        <button @click="closeDetail"
                            class="w-full bg-blue-500 text-white py-1.5 sm:py-2 px-3 sm:px-4 rounded-lg hover:bg-blue-600 transition-colors duration-200 flex items-center justify-center text-[11px] sm:text-xs md:text-sm">
                            <svg xmlns="http://www.w3.org/2000/svg"
                                class="h-3.5 w-3.5 sm:h-4 sm:w-4 md:h-5 md:w-5 mr-1.5 sm:mr-2" fill="none"
                                viewBox="0 0 24 24" stroke="currentColor">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                    d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                            </svg>
                            닫기
                        </button>
                    </div>
                </div>
            </div>
        </transition>
    </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, shallowRef } from 'vue';
import noticeAPI from '@/api/notice/notice';

// 메모이제이션된 debounce 함수
const debounce = (fn, delay) => {
    let timeoutId;
    return (...args) => {
        if (timeoutId) clearTimeout(timeoutId);
        timeoutId = setTimeout(() => fn(...args), delay);
    };
};

// 공지사항 관련 상태
const notices = shallowRef([]);
const totalNotices = ref(0);
const currentNoticePage = ref(1);
const itemsPerPage = 7;
const selectedNotice = shallowRef(null);
const isLoading = ref(false);

// 캐시 관련 상수
const CACHE_SIZE_LIMIT = 5; // 최대 캐시 페이지 수
const CACHE_EXPIRY_TIME = 5 * 60 * 1000; // 5분

// 캐시 데이터 구조
const noticeCache = new Map();

// 캐시 관리 함수
const manageCache = (page, data) => {
    // 캐시 크기 제한 확인
    if (noticeCache.size >= CACHE_SIZE_LIMIT) {
        // 가장 오래된 캐시 삭제
        const oldestKey = noticeCache.keys().next().value;
        noticeCache.delete(oldestKey);
    }

    // 캐시 데이터 저장 (타임스탬프 포함)
    noticeCache.set(page, {
        data,
        timestamp: Date.now()
    });
};

// 캐시 데이터 조회
const getCachedData = (page) => {
    const cached = noticeCache.get(page);
    if (!cached) return null;

    // 캐시 만료 확인
    if (Date.now() - cached.timestamp > CACHE_EXPIRY_TIME) {
        noticeCache.delete(page);
        return null;
    }

    return cached.data;
};

// window width 상태 관리 (디바운스 적용)
const windowWidth = ref(window.innerWidth);
const handleResize = debounce(() => {
    windowWidth.value = window.innerWidth;
}, 250);

// 날짜 포맷터 초기화 (메모이제이션)
const dateFormatter = new Intl.DateTimeFormat('ko-KR', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
});

// 이미지 최적화 (메모이제이션)
const optimizeContent = (html) => {
    if (!html) return '';
    return html.replace(/<img/g, '<img loading="lazy"');
};

// 공지사항 조회 (캐싱 적용)
const fetchNotices = async (page = currentNoticePage.value) => {
    try {
        isLoading.value = true;
        const response = await noticeAPI.getNotices(page, itemsPerPage);
        notices.value = response.data.notices;
        totalNotices.value = response.data.total;
        
        // 데이터 캐싱 (서버에서 받은 읽음 상태 유지)
        manageCache(page, response.data.notices);
    } catch (error) {
        console.error('공지사항 조회 중 오류 발생:', error);
    } finally {
        isLoading.value = false;
    }
};

// 상세 보기 닫기 (최적화)
const closeDetail = () => {
    selectedNotice.value = null;
};

// 상세 보기 열기 (최적화)
const openDetail = async (notice) => {
    try {
        // 이미 읽은 공지사항인 경우 상세 조회만 실행
        if (notice.is_read) {
            const detailResponse = await noticeAPI.getNoticeDetail(notice.id);
            selectedNotice.value = {
                ...detailResponse.data,
                content: optimizeContent(detailResponse.data.content)
            };
        } else {
            // 읽지 않은 공지사항인 경우 병렬로 처리
            const [detailResponse] = await Promise.all([
                noticeAPI.getNoticeDetail(notice.id),
                noticeAPI.markAsRead(notice.id)
            ]);

            selectedNotice.value = {
                ...detailResponse.data,
                content: optimizeContent(detailResponse.data.content)
            };

            // 목록 업데이트 최적화
            notices.value = notices.value.map(n => {
                if (n.id === notice.id) {
                    return {
                        ...n,
                        is_read: true,
                        read_date: new Date().toISOString()
                    };
                }
                return n;
            });
            
            // 캐시된 데이터도 업데이트
            const currentPage = currentNoticePage.value;
            const cachedData = getCachedData(currentPage);
            if (cachedData) {
                const updatedNotices = cachedData.map(n => {
                    if (n.id === notice.id) {
                        return {
                            ...n,
                            is_read: true,
                            read_date: new Date().toISOString()
                        };
                    }
                    return n;
                });
                manageCache(currentPage, updatedNotices);
            }
        }
        
        // URL에 상세 조회 상태 추가
        history.pushState({ type: 'notice', id: notice.id }, '', `#notice-${notice.id}`);
    } catch (error) {
        console.error('공지사항 상세 조회 중 오류 발생:', error);
    }
};

// 페이지네이션 계산 (메모이제이션)
const totalNoticePages = computed(() => Math.ceil(totalNotices.value / itemsPerPage));
const paginatedNotices = computed(() => notices.value);

// 페이지 변경 시 공지사항 다시 조회 (캐싱 활용)
const handlePageChange = debounce((page) => {
    currentNoticePage.value = page;
    fetchNotices(page);
}, 100);

// 날짜 포맷 (메모이제이션)
const formatDate = (dateString) => {
    if (!dateString) return '';
    return dateFormatter.format(new Date(dateString));
};

// 2주 이내 게시물 체크 (메모이제이션)
const twoWeeksAgo = computed(() => {
    const date = new Date();
    date.setDate(date.getDate() - 10);
    return date;
});

const isNew = (date) => {
    if (!date) return false;
    return new Date(date) > twoWeeksAgo.value;
};

// 페이지네이션 visible 계산 (메모이제이션)
const getTotalVisible = computed(() => {
    if (windowWidth.value >= 1024) return 7;
    if (windowWidth.value >= 768) return 5;
    return 3;
});

// 첨부파일 다운로드 함수 추가
const downloadAttachment = (attachment) => {
    if (!attachment?.file_name) return;
    
    const defaultUrl = process.env.VUE_APP_BEFORE_API_URL;
    const url = `${defaultUrl}/api/vmd_service/notice/getAttachmentDownload?file_name=${attachment.file_name}`;
    
    // 다운로드 링크 생성 및 클릭
    const link = document.createElement('a');
    link.href = url;
    link.download = attachment.original_name || attachment.file_name;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
};

// 뒤로가기 처리 (디바운스 적용)
const handlePopState = debounce(() => {
    if (selectedNotice.value) {
        closeDetail();
    }
}, 100);

// 초기 데이터 로드 (최적화)
onMounted(() => {
    fetchNotices();
    window.addEventListener('resize', handleResize);
    window.addEventListener('popstate', handlePopState);
});

onUnmounted(() => {
    window.removeEventListener('resize', handleResize);
    window.removeEventListener('popstate', handlePopState);
});
</script>

<style scoped>
.slide-enter-active,
.slide-leave-active {
    transition: transform 0.3s ease;
}

.slide-enter-from,
.slide-leave-to {
    transform: translateX(100%);
}

.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
    opacity: 0;
}

:deep(.prose) {
    max-width: none;
    color: #374151;
}

:deep(.prose p) {
    margin-top: 1.25em;
    margin-bottom: 1.25em;
}

@media (min-width: 640px) {
    :deep(.pagination-text) {
        font-size: 12px;
    }

    :deep(.pagination-text .v-btn) {
        font-size: 12px;
    }
}

@media (min-width: 1024px) {
    :deep(.pagination-text) {
        font-size: 14px;
    }

    :deep(.pagination-text .v-btn) {
        font-size: 14px;
    }
}

@media (min-width: 1280px) {
    :deep(.pagination-text) {
        font-size: 15px;
    }

    :deep(.pagination-text .v-btn) {
        font-size: 15px;
    }
}

/* NEW 뱃지 더 작게 */
.bg-red-500.text-white.text-\[10px\] {
    font-size: 10px !important;
    padding: 0 4px !important;
    height: 16px;
    min-width: 22px;
    display: flex;
    align-items: center;
    justify-content: center;
}
</style>
